/**
 * WAYP-289: Enterprise Feature Toggle — Client Hooks
 * useEnterpriseFeatures: load feature list for caller's org
 * useFeature: check if a specific feature is enabled
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useDeferredReady } from './use-deferred-ready';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type FeatureCategory =
  | 'quotes'
  | 'maps'
  | 'calendar'
  | 'customer'
  | 'custom_reports'
  | 'driver_carrier'
  | 'equipment'
  | 'dispatch'
  | 'finance'
  | 'fuel'
  | 'pwa'
  | 'general'
  | 'api_tendering'
  | 'edi'
  | 'brokerage'
  | 'modules'
  // ── Module-specific sidebar keys (each module has its own settings page) ──
  | 'mod_dispatch_freight'
  | 'mod_usps'
  | 'mod_usps_pwa'
  | 'mod_intermodal'
  | 'mod_intermodal_pwa'
  | 'mod_ftl_brokerage'
  | 'mod_ftl_brokerage_pwa'
  | 'mod_livestock'
  | 'mod_livestock_pwa';

export interface PreferenceField {
  key: string;
  label: string;
  // 'user-select' (feat/driver-onboarding): single-select dropdown of the
  // current org's users; the stored value is the chosen user's id.
  type: 'toggle' | 'select' | 'multi_select' | 'input' | 'number' | 'textarea' | 'color' | 'date' | 'link' | 'grid' | 'repeatable' | 'user-select';
  default?: unknown;
  options?: string[];
  min?: number;
  max?: number;
  /** `number` fields only — reject non-whole values (e.g. hour-count settings
   *  where "2.5" or "-1" make no sense). Combined with `min` it enforces a
   *  positive-integer-only field (min omitted defaults the message to 1). */
  integer?: boolean;
  required?: boolean;
  description?: string;
  /** Optional V1-style section grouping — fields sharing a `section` render
   *  under one header (e.g. Carrier Portal: General / Onboarding / Bid Requests). */
  section?: string;
  dependsOn?: string;
  /** A single expected value, '*' for "any non-empty value", or an array
   *  of values to OR-match against (e.g. rate_type ∈ [lane modes]). */
  dependsOnValue?: unknown;
  /** Extra AND-conditions on sibling field values — ALL must match for this
   *  field to render (complements the single `dependsOn`). Used to gate the
   *  per-provider HOS fields on the `use_hos_for_shift` toggle. */
  visibleWhen?: Array<{ key: string; value: unknown }>;
  // grid type: rows × columns of selects — e.g. V1 "When driver arrives/departs" × "pick/drop"
  rows?: Array<{ key: string; label: string }>;
  columns?: Array<{ key: string; label: string }>;
  // repeatable type: array of rows, each row has its own sub-fields matching rowSchema.
  // Value is Array<Record<string, unknown>>. Used for EDI 214 Alert, Custom Module tables.
  rowSchema?: PreferenceField[];
  addRowLabel?: string;
}

export interface EnterpriseFeatureRecord {
  id: string;
  key: string;
  name: string;
  description: string;
  category: FeatureCategory;
  defaultEnabled: boolean;
  isPremium: boolean;
  requiresSetup: boolean;
  parentFeatureKey: string | null;
  preferencesSchema: PreferenceField[];
  // Merged org state:
  isEnabled: boolean;
  configJson: Record<string, unknown>;
  enabledAt: string | null;
  enabledBy: string | null;
}

export interface OrgFeatureToggle {
  isEnabled: boolean;
  configJson?: Record<string, unknown>;
}

export interface BulkFeatureToggle {
  features: Array<{ featureKey: string; isEnabled: boolean }>;
}

// ─── Query Keys ────────────────────────────────────────────────────────────────

export const featureKeys = {
  all: ['enterprise-features'] as const,
  myOrg: () => [...featureKeys.all, 'my-org'] as const,
  adminAll: () => [...featureKeys.all, 'admin', 'all'] as const,
  adminOrg: (orgId: string) => [...featureKeys.all, 'admin', 'org', orgId] as const,
};

// ─── Own-org hooks (authenticated users) ──────────────────────────────────────

/** Load all enterprise features for the caller's org (with enabled state) */
export function useEnterpriseFeatures() {
  return useQuery({
    queryKey: featureKeys.myOrg(),
    queryFn: async () => {
      const res = await api.get<{ data: EnterpriseFeatureRecord[] }>('/features');
      return res.data.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Check whether a specific feature key is enabled for the current user's org.
 * Returns false while loading to avoid flash of enabled content.
 */
export function useFeature(featureKey: string): boolean {
  const { data } = useEnterpriseFeatures();
  if (!data) return false;
  // Match by slug key OR display name. Several dispatch gates call this with
  // the human name (e.g. `useFeature('Enable Routing Row Link')`) while the
  // seed stores a slug key (`enable_routing_row_link`); tolerating both keeps
  // every gate working regardless of which string the call site passes.
  const feature = data.find((f) => f.key === featureKey || f.name === featureKey);
  return feature?.isEnabled ?? false;
}

/**
 * Read a preference value from an enterprise feature's configJson.
 * Returns the stored value, or `defaultValue` if not found / feature disabled.
 */
/**
 * ATL-001468 item 3 — boolean preference read that accepts MULTIPLE key
 * spellings (the V2 short key + the verbose V1-import key) and coerces
 * V1-style truthy values (1 / '1' / true). First defined key wins.
 * Returns `defaultValue` when the feature is off or no key is set.
 */
export function useBooleanFeaturePreference(
  featureKey: string,
  prefKeys: ReadonlyArray<string>,
  defaultValue = false,
): boolean {
  const { data } = useEnterpriseFeatures();
  if (!data) return defaultValue;
  const feature = data.find((f) => f.key === featureKey || f.name === featureKey);
  if (!feature?.isEnabled) return defaultValue;
  for (const k of prefKeys) {
    const val = feature.configJson?.[k];
    if (val !== undefined && val !== null && val !== '') {
      return val === true || val === 1 || val === '1';
    }
  }
  return defaultValue;
}

export function useFeaturePreference<T = string>(
  featureKey: string,
  prefKey: string,
  defaultValue: T = '' as unknown as T,
): T {
  const { data } = useEnterpriseFeatures();
  if (!data) return defaultValue;
  // Match by slug key OR display name — see useFeature for rationale.
  const feature = data.find((f) => f.key === featureKey || f.name === featureKey);
  if (!feature?.isEnabled) return defaultValue;
  const val = feature.configJson?.[prefKey];
  if (val === undefined || val === null || val === '') return defaultValue;
  return val as T;
}

/**
 * ATL-000705 — per-SCREEN Terminal-filter gate (V1 `Helpers::isFilterEnabled`).
 *
 * `enabled` is true only when `enable_filters` is ON for the org AND the
 * screen's 'Yes'/'No' configJson key resolves to 'Yes' — default-ON when
 * unset (`?? 'Yes'`), matching V1 + the seed defaults + the server-side
 * `isScreenFilterEnabled`. `label` is the org's configured filter word
 * (`terminal_filter_name`, default "Terminal") for headers/placeholders.
 *
 * Returns `enabled: false` while features are loading (no flash), same
 * stance as `useFeature`. NB the pre-existing trips/contracts/equipment
 * gates inline a STRICT `=== 'Yes'` (hidden when the key is missing) —
 * deliberately left untouched per the ticket's acceptance note.
 */
export function useTerminalFilterGate(screenKey: string): {
  enabled: boolean;
  label: string;
} {
  const { data } = useEnterpriseFeatures();
  const feature = data?.find((f) => f.key === 'enable_filters');
  const label =
    (typeof feature?.configJson?.terminal_filter_name === 'string' &&
      (feature.configJson.terminal_filter_name as string).trim()) ||
    'Terminal';
  if (feature?.isEnabled !== true) return { enabled: false, label };
  return {
    enabled: ((feature.configJson?.[screenKey] as string | undefined) ?? 'Yes') === 'Yes',
    label,
  };
}

// ─── Admin hooks (super admin) ────────────────────────────────────────────────

/** Super admin: list all organizations for the org selector */
export function useOrganizations() {
  // #1429: org-switcher data in the header — non-critical, defer off the
  // cold-load critical path.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: ['admin', 'organizations'] as const,
    queryFn: async () => {
      const res = await api.get<{ data: Array<{ id: string; name: string; slug: string }> }>(
        '/admin/features/organizations',
      );
      return res.data.data;
    },
    enabled: deferredReady,
    staleTime: 1000 * 60 * 10, // 10 min
  });
}

/** Super admin: list all feature definitions */
export function useAllFeatures() {
  return useQuery({
    queryKey: featureKeys.adminAll(),
    queryFn: async () => {
      const res = await api.get<{ data: EnterpriseFeatureRecord[] }>('/admin/features');
      return res.data.data;
    },
  });
}

/** Super admin: list features with org override states for a specific org */
export function useFeaturesForOrg(organizationId: string) {
  return useQuery({
    queryKey: featureKeys.adminOrg(organizationId),
    queryFn: async () => {
      const res = await api.get<{ data: EnterpriseFeatureRecord[] }>(
        `/admin/features/org/${organizationId}`,
      );
      return res.data.data;
    },
    enabled: !!organizationId,
  });
}

/** Super admin: toggle a feature for an org */
export function useToggleOrgFeature(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      featureKey,
      isEnabled,
      configJson,
    }: {
      featureKey: string;
      isEnabled: boolean;
      configJson?: Record<string, unknown>;
    }) => {
      const res = await api.put(`/admin/features/org/${organizationId}/${featureKey}`, {
        isEnabled,
        configJson,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.adminOrg(organizationId) });
      queryClient.invalidateQueries({ queryKey: featureKeys.myOrg() });
    },
  });
}

/** Super admin: bulk toggle multiple features for an org */
export function useBulkToggleOrgFeatures(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkFeatureToggle) => {
      const res = await api.post(`/admin/features/org/${organizationId}/bulk`, input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.adminOrg(organizationId) });
      queryClient.invalidateQueries({ queryKey: featureKeys.myOrg() });
    },
  });
}

/**
 * WAYP-820: Self-service org-scoped feature config update. Authenticated user
 * patches their OWN org's `OrganizationFeature.configJson` for a given feature
 * key — no admin permission required, no organizationId in the URL (server
 * reads from req.user). Used by the Routing Row Fields matrix on the Company
 * Preferences page.
 */
export function useUpdateMyOrgFeatureConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      featureKey,
      configJson,
    }: {
      featureKey: string;
      configJson: Record<string, unknown>;
    }) => {
      const res = await api.patch(`/features/${featureKey}/config`, { configJson });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.myOrg() });
    },
  });
}

/**
 * ATL-367: Self-service org-scoped feature enable/disable. Company Admins
 * (settings:manage) toggle an ALLOWLISTED feature (today: driver_expiries)
 * for their own org — the Driver Expiries card on Company Preferences owns
 * the master toggle so a super-admin is no longer required to switch it on.
 */
export function useToggleMyOrgFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ featureKey, isEnabled }: { featureKey: string; isEnabled: boolean }) => {
      const res = await api.patch(`/features/${featureKey}/toggle`, { isEnabled });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.myOrg() });
    },
  });
}

/** Super admin: update config for a feature on an org */
export function useUpdateFeatureConfig(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      featureKey,
      configJson,
    }: {
      featureKey: string;
      configJson: Record<string, unknown>;
    }) => {
      const res = await api.patch(
        `/admin/features/org/${organizationId}/${featureKey}/config`,
        { configJson },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.adminOrg(organizationId) });
      // Also refresh the caller's OWN-org feature cache so feature-gated UI
      // (e.g. the New Invoice "Create all invoices" button, which reads
      // `useEnterpriseFeatures()` → featureKeys.myOrg()) reflects a config
      // change immediately, without a 5-min staleTime wait or hard refresh.
      // Mirrors `useToggleOrgFeature`, which already invalidates both.
      queryClient.invalidateQueries({ queryKey: featureKeys.myOrg() });
    },
  });
}
